import { useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Danger,
  SuccessHeader,
} from '@unified-checkout/design-system/icons/state_icons';

import type { TState } from '@app/dashboard/types/innerLayout/status-content';
import type { IPlan } from '@app/dashboard/types/innerLayout/reach/bundles';
import {
  REACH_PAGE_EVENTS,
  REACH_PAGE_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/reach';
import { useLangStore } from '@app/dashboard/store/lang';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { StatusContent } from '@app/dashboard/components/inner_layout/StatusContent';
import { MODAL_STEP_STATUS } from '@app/dashboard/constants/reach/campaigns';
import { subscribeToPlan } from '@app/dashboard/services/inner_layout/reach/bundles';
import { getPlanExpireDate, getPlanName } from '../../../../utils';

import { ConfirmationModal } from '../../../../components/ConfirmationModal';

import styles from './SubscriptionModal.module.scss';

const SubscriptionModal = ({
  modalConfig,
  setModalConfig,
  onLoadingChange,
}: {
  modalConfig: { isOpen: boolean; plan: IPlan | null };
  setModalConfig: Dispatch<
    SetStateAction<{ isOpen: boolean; plan: IPlan | null }>
  >;
  onLoadingChange?: (isLoading: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const [modalStep, setModalStep] = useState({
    type: MODAL_STEP_STATUS.START,
    message: '',
  });

  const { mutate: subscribeToPlanMutate, isPending: isSubscribeToPlanLoading } =
    useMutation({
      mutationFn: subscribeToPlan,
      onSuccess: (res) => {
        const data = res?.data?.data?.data?.last_sms_subscibed_plans_history;

        amplitude.track(
          REACH_PAGE_EVENTS.BUNDLES_PAGE.SUBSCRIBE_BUNDLE_SUCCEEDED,
          {
            [REACH_PAGE_EVENTS_PROPERTIES.BUNDLES_PAGE.BUNDLE_PLAN]:
              modalConfig?.plan?.name_en || '',
          }
        );

        handleModalStatus({
          type: MODAL_STEP_STATUS.SUCCESS,
          message: t(
            'innerLayout.reach.bundles.modal.subscribe.successMessage',
            {
              name: getPlanName({ plan: data.plan }, lang),
              sms: parseInt(data.plan?.amount),
              date: getPlanExpireDate(data.plan?.expire_in || 0),
            }
          ),
        });
      },
      onError: (
        axiosErr: AxiosError<{ message?: string; data?: { message?: string } }>
      ) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data?.data?.message === 'balance_insufficient') {
          message = t(
            'innerLayout.reach.bundles.modal.subscribe.insufficientBalanceMessage'
          );
        } else if (axiosErr.response?.data?.message) {
          message = axiosErr.response?.data?.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }

        amplitude.track(
          REACH_PAGE_EVENTS.BUNDLES_PAGE.SUBSCRIBE_BUNDLE_FAILED,
          {
            [REACH_PAGE_EVENTS_PROPERTIES.BUNDLES_PAGE.BUNDLE_PLAN]:
              modalConfig?.plan?.name_en || '',
            [REACH_PAGE_EVENTS_PROPERTIES.SHARED.ERROR_MESSAGE]: message,
          }
        );

        setModalStep({ type: MODAL_STEP_STATUS.FAILED, message });
      },
    });

  const handleModalStatus = ({
    type,
    message,
  }: {
    type: string;
    message: string;
  }) => {
    setModalStep({ type, message });

    setTimeout(() => {
      setModalConfig((prev) => ({ ...prev, isOpen: false }));
      if (type !== MODAL_STEP_STATUS.SUCCESS) {
        setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
      }
    }, 3000);
  };

  const handleClose = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
    setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
  };

  const handleRenderContent = () => {
    switch (modalStep.type) {
      case MODAL_STEP_STATUS.START:
        return (
          <div className={styles['bundles__plans-modal-content']}>
            <p className={styles['bundles__plans-modal-content-text']}>
              {t('innerLayout.reach.bundles.modal.subscribe.upgradeNote', {
                date: getPlanExpireDate(modalConfig.plan?.expire_in || 0),
              })}
            </p>
          </div>
        );
      case MODAL_STEP_STATUS.SUCCESS:
      case MODAL_STEP_STATUS.FAILED:
        return (
          <StatusContent
            state={modalStep.type as TState}
            title={
              modalStep.type === MODAL_STEP_STATUS.SUCCESS
                ? null
                : t(modalStep.message)
            }
            supportingText={
              modalStep.type === MODAL_STEP_STATUS.SUCCESS
                ? modalStep.message
                : t('innerLayout.reach.bundles.modal.subscribe.failedNote')
            }
            {...(modalStep.type === MODAL_STEP_STATUS.FAILED ? {} : {})}
            statusIcon={
              modalStep.type === MODAL_STEP_STATUS.SUCCESS
                ? SuccessHeader
                : Danger
            }
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!modalConfig.isOpen && modalStep.type === MODAL_STEP_STATUS.SUCCESS) {
      navigate(INNER_ROUTES.CAMPAIGNS);
    }
  }, [modalConfig.isOpen, modalStep.type]);

  useEffect(() => {
    if (
      modalConfig.plan?.free_plan &&
      modalStep.type === MODAL_STEP_STATUS.START &&
      modalConfig.isOpen
    ) {
      subscribeToPlanMutate({
        plan_id: modalConfig.plan.id,
        platform: 'dashboard',
      });
    }
  }, [modalConfig]);

  useEffect(() => {
    onLoadingChange?.(isSubscribeToPlanLoading);
  }, [isSubscribeToPlanLoading]);

  return (
    <div className={styles['bundles__plans-modal']}>
      <ConfirmationModal
        open={
          modalConfig.plan?.free_plan
            ? modalStep.type !== MODAL_STEP_STATUS.START
            : modalConfig.isOpen
        }
        modalContent={{
          title: t('innerLayout.reach.bundles.modal.subscribe.title'),
          ...(modalStep.type === MODAL_STEP_STATUS.START
            ? {
                supportingText: t(
                  'innerLayout.reach.bundles.modal.subscribe.subtitle',
                  {
                    name: getPlanName(
                      { plan: modalConfig.plan as IPlan },
                      lang
                    ),
                    price: modalConfig.plan?.price,
                  }
                ),
              }
            : {}),
        }}
        primaryBtn={{
          text: t('innerLayout.reach.bundles.modal.subscribe.confirmBtn'),
          color: 'primary',
          onClick: () => {
            subscribeToPlanMutate({
              plan_id: modalConfig.plan?.id as number,
              platform: 'dashboard',
            });
          },
          loading: isSubscribeToPlanLoading,
          disabled: isSubscribeToPlanLoading,
        }}
        secondaryBtn={{
          text: t('innerLayout.reach.bundles.modal.subscribe.cancelBtn'),
          disabled: isSubscribeToPlanLoading,
        }}
        isModalStateContent={modalStep.type !== MODAL_STEP_STATUS.START}
        hasCloseIcon={modalStep.type !== MODAL_STEP_STATUS.START}
        handleClose={handleClose}
      >
        {handleRenderContent()}
      </ConfirmationModal>
    </div>
  );
};

export default SubscriptionModal;
